/**
 * O endereço da página de Pagamento (Configurações → Pagamento) e o
 * `?unidade=<id>` que leva até ela.
 *
 * O botão do e-mail da cobrança abre `/configuracoes/pagamento?unidade=<id>`
 * (`docs/inter/contrato-das-telas.md`, "O e-mail da cobrança"). Quatro lugares
 * da tela tratam esse endereço, e as quatro regras moram aqui, puras (sem
 * React, sem `window`, sem armazenamento):
 *
 *   • `enderecoDoPagamento`: o endereço montado (o toque no aviso da
 *     mensalidade e o antigo `/mensalidade`). Só um id com forma de uuid entra.
 *   • `unidadeDoLink`: o que o `SettingsLayout` faz com o `?unidade` que chegou
 *     (D-B3). O id de uma unidade da pessoa vira o escopo; qualquer outro valor
 *     volta à escolha da unidade ("Todas as unidades"), e quem tem uma unidade
 *     só fica na dela. Nos dois casos o parâmetro sai do endereço, para o
 *     seletor do topo voltar a mandar.
 *   • `voltaDoLogin`: para onde o login devolve quem chegou sem sessão (D-B4).
 *     Só a página de Pagamento, e só pelo estado do roteador (o `from` que a
 *     rota protegida põe), nunca pelo endereço: um link de fora não escolhe
 *     para onde o login manda.
 *
 * Quem decide o que a pessoa vê continua sendo o servidor (403 para a unidade
 * que não é dela). Aqui só se evita mostrar a tela errada antes da resposta.
 */

import type { Tenant } from "@/types/index"
import { TODAS_AS_UNIDADES, unidadeUnica } from "@/lib/escopoDaUnidade"

/** O caminho da página de Pagamento. */
export const CAMINHO_DO_PAGAMENTO = "/configuracoes/pagamento"

const RE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** O valor tem a forma de um id de unidade (uuid)? */
export function ehUuid(valor: unknown): valor is string {
  return typeof valor === "string" && RE_UUID.test(valor)
}

/** O caminho é o da página de Pagamento (com ou sem a barra no fim)? */
export function ehOCaminhoDoPagamento(caminho: unknown): boolean {
  return typeof caminho === "string" && caminho.replace(/\/+$/, "") === CAMINHO_DO_PAGAMENTO
}

/** `/configuracoes/pagamento?unidade=<id>` quando o id tem forma de uuid; senão, só a página. */
export function enderecoDoPagamento(unidadeId: unknown): string {
  return ehUuid(unidadeId) ? `${CAMINHO_DO_PAGAMENTO}?unidade=${unidadeId.toLowerCase()}` : CAMINHO_DO_PAGAMENTO
}

export interface UnidadeDoLink {
  /** O que vai para `setSelectedScope`: o id da unidade, ou "Todas as unidades". */
  escopo: string
  /** A busca do endereço sem o `unidade` (o resto dos parâmetros fica), com o "?" quando sobra algo. */
  busca: string
}

/**
 * O que fazer com o `?unidade` da página de Pagamento. `null`: o endereço não
 * traz o parâmetro, e nada muda.
 *
 * O id de uma unidade da pessoa vira o escopo. Qualquer outro valor (id de
 * outra unidade, id sem forma de uuid, apelido, vazio) volta à escolha: para
 * quem tem várias unidades, "Todas as unidades", e a página pede a escolha;
 * para quem tem uma só, a dela (a invariante de `escopoDaUnidade`).
 */
export function unidadeDoLink(busca: string, unidades: Tenant[]): UnidadeDoLink | null {
  const parametros = new URLSearchParams(busca)
  if (!parametros.has("unidade")) return null
  const pedida = (parametros.get("unidade") ?? "").trim().toLowerCase()
  parametros.delete("unidade")
  const resto = parametros.toString()
  const daPessoa = ehUuid(pedida) ? unidades.find((u) => u.id.toLowerCase() === pedida) : undefined
  return {
    escopo: daPessoa ? daPessoa.id : (unidadeUnica(unidades)?.id ?? TODAS_AS_UNIDADES),
    busca: resto ? `?${resto}` : "",
  }
}

const ehObjeto = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v)

/**
 * Para onde o login devolve a pessoa, lido do estado do roteador
 * (`location.state.from`, posto pela rota protegida). Só a página de
 * Pagamento volta, com o `?unidade` quando ele tem forma de uuid; qualquer
 * outro destino é `null`, e o login segue para onde sempre foi.
 */
export function voltaDoLogin(estado: unknown): string | null {
  const de = ehObjeto(estado) ? estado.from : null
  if (!ehObjeto(de) || !ehOCaminhoDoPagamento(de.pathname)) return null
  const busca = typeof de.search === "string" ? de.search : ""
  return enderecoDoPagamento(new URLSearchParams(busca).get("unidade")?.trim())
}
